<template>
    <div
        class="banner"
        :class="useType"
        :style="{
            minHeight: minHeight,
            backgroundColor: isColor ? backgroundColor : ''
        }"
        @click="bannerClick"
    >
        <site-img
            class="single-picture"
            v-if="isSingle"
            type="bgImg"
            :src="(usePicture && usePicture.imgUrl) || usePicture || ''"
            :alt="(usePicture && usePicture.alt) || ''"
            :persent="1"
        />
        <pictures
            v-if="isMulti && pictureList.length"
            :pictureList="pictureList"
            :rawDot="rawDot"
            :showPagination="
                isMobileSize && useMobileSettings
                    ? mobilePictureListOption.showPagination || false
                    : pictureListOption.showPagination || false
            "
            :options="
                isMobileSize && useMobileSettings
                    ? mobilePictureListOption.swiperOption
                    : pictureListOption.swiperOption
            "
        />
        <banner-video
            ref="bannerVideo"
            v-if="isVideo"
            :key="videoKey"
            :src="videoSrc"
            :previewSrc="previewSrc"
            :option="
                isMobileSize && useMobileSettings
                    ? mobileVideoOption
                    : videoOption
            "
            @updateRatio="updateVideoRatio"
            @updatePlay="updateVideoPlay"
        />
        <slot></slot>
        <div
            v-show="maskShow && !emptyBgColor"
            class="banner-mask"
            :style="{
                opacity: maskOption.maskOpacity || '',
                background: (maskOption.maskColor || '').replace(';', '')
            }"
        ></div>
        <div v-if="emptyBgColor && !hasBg" class="hero-default default-wrap" :style="{ background: emptyBgColor}">
            <site-img :src="defaultIcon" dir="width" />
        </div>
    </div>
</template>

<script>
import util from '../common/util.js';
import global from '../common/global.js';
import { defineAsyncComponent } from 'vue';

export const BANNER_TYPE = {
    single: 'single',
    scale: 'scale',
    fullScreen: 'fullScreen',
    height: 'height'
}

export default {
    emits: ['updateVideoPlay'],
    props: {
        type: {
            type: String,
            default: BANNER_TYPE.single
        },
        mobileType: {
            type: String,
            default: ''
        },
        useMobileSettings: {
            type: Boolean,
            default: false
        },
        sizeOption: {
            type: Object,
            default: () => ({
                type: 'scale',
                mobileType: 'scale',
                ratio: 33.33,
                mobileRatio: 66.7,
                height: 300,
                mobileHeight: 300
            })
        },
        bgColor: {
            type: String,
            default: ''
        },
        mobileBgColor: {
            type: String,
            default: ''
        },
        pictureOption: {
            type: Object,
            default: () => ({
                src: '',
                mobileSrc: ''
            })
        },
        mobilePicture: {
            type: [String, Object],
            default: ''
        },
        pictureListOption: {
            type: Object,
            default: () => ({
                list: [],
                mobileList: [],
                showPagination: false,
                swiperOption: {}
            })
        },
        mobilePictureListOption: {
            type: Object,
            default: () => ({
                list: [],
                showPagination: false,
                swiperOption: {}
            })
        },
        maskOption: {
            type: Object,
            default: () => ({
                showMask: false,
                maskColor: '#000000',
                maskOpacity: 0.15
            })
        },
        videoOption: {
            type: Object,
            default: () => ({
                autoplay: false,
                src: '',
                previewSrc: '',
                playInline: true,
                showProgress: true,
                videoMuted: true,
                showMuteButton: true,
                autoHeight: false,
                hidePlayButton: false,
                volume: null
            })
        },
        mobileVideoOption: {
            type: Object,
            default: () => ({
                autoplay: false,
                src: '',
                previewSrc: '',
                playInline: true,
                showProgress: true,
                videoMuted: true,
                autoHeight: false,
                hidePlayButton: false,
                volume: null
            })
        },
        rawDot: {
            type: Boolean,
            default: false
        },
        emptyBgColor: {
            type: String,
            default: ''
        },
        defaultIcon: {
            type: String,
            default: 'https://cdn.chime.me/image/fs/sitebuild/site-cms/default-img.png'
        },
        getBannerStyle: {
            type: Function,
            default: null
        }
    },
    data: function() {
        return {
            minHeight: '',
            videoKey: new Date().getTime(),
            videoRatio: 0,
            videoPlay: false,
        };
    },
    computed: {
        isMobileSize() {
            return this.platform.isMobile
        },
        useType() {
            if (this.useMobileSettings && this.isMobileSize) {
                return this.mobileType || this.type;
            } else {
                return this.type;
            }
        },
        isColor() {
            return this.useType == 'color';
        },
        isMulti() {
            return this.useType == 'multi';
        },
        isSingle() {
            return this.useType == 'single';
        },
        isVideo() {
            return this.useType == 'video';
        },
        maskShow({ isColor }) {
            let maskShow = this.maskOption.showMask;
            if (this.isVideo && this.videoOption.autoplay) {
                maskShow = false;
            }
            if (isColor) {
                maskShow = false
            }
            return maskShow;
        },
        backgroundColor() {
            if (this.useMobileSettings && this.isMobileSize) {
                return this.mobileBgColor || this.bgColor;
            } else {
                return this.bgColor;
            }
        },
        usePicture() {
            let mobileSrc = this.getSrc(this.pictureOption.mobileSrc);
            let pcSrc = this.getSrc(this.pictureOption.src);
            if (this.isMobileSize) {
                if (this.useMobileSettings) {
                    return this.mobilePicture || mobileSrc || pcSrc || '';
                } else {
                    return mobileSrc || pcSrc || '';
                }
            } else {
                return pcSrc || mobileSrc || '';
            }
        },

        pictureList() {
            let list = [];
            if (this.isMobileSize) {
                if (this.useMobileSettings) {
                    list =
                        this.mobilePictureListOption.mobileList &&
                        this.mobilePictureListOption.mobileList.length > 0
                            ? this.mobilePictureListOption.mobileList
                            : this.pictureListOption.mobileList &&
                              this.pictureListOption.mobileList.length > 0
                                ? this.pictureListOption.mobileList
                                : this.pictureListOption.list;
                } else {
                    list =
                        this.pictureListOption.mobileList &&
                        this.pictureListOption.mobileList.length > 0
                            ? this.pictureListOption.mobileList
                            : this.pictureListOption.list;
                }
            } else {
                list =
                    this.pictureListOption.list &&
                    this.pictureListOption.list.length > 0
                        ? this.pictureListOption.list
                        : this.pictureListOption.mobileList;
            }
            return (list || []).map(item => {
                if (typeof item.imgUrl === 'object') {
                    return item.imgUrl;
                }
                return item;
            });
        },
        videoSrc() {
            if (this.useMobileSettings && this.isMobileSize) {
                return this.mobileVideoOption.src || this.videoOption.src;
            } else {
                return this.videoOption.src;
            }
        },
        previewSrc() {
            if (this.useMobileSettings && this.isMobileSize) {
                return (
                    this.mobileVideoOption.previewSrc ||
                    this.videoOption.previewSrc
                );
            } else {
                return this.videoOption.previewSrc;
            }
        },
        hasBg() {
            if (this.isSingle) {
                return this.usePicture;
            } else if (this.isMulti) {
                return this.pictureList?.length;
            } else {
                return this.previewSrc || this.videoSrc;
            }
        }
    },
    watch: {
        sizeOption: {
            deep: true,
            handler() {
                this.calcStyle(true);
            }
        },
        videoOption() {
            this.refreshVideo();
        },
        videoPlay() {
            this.calcStyle(true);
            this.$nextTick(() => {
                this.$refs.bannerVideo &&
                    this.$refs.bannerVideo.calcVideoStyle();
            });
        }
    },
    created() {
        if (util.isSSRServer) {
            const sizeOption = this.sizeOption || {};
            let type = sizeOption.type;
            let height = sizeOption.height;
            let minHeight = '640px';
            if (this.isMobileSize) {
                type = sizeOption.mobileType;
                height = sizeOption.mobileHeight;
                minHeight = '280px';
            }
            if (type === BANNER_TYPE.height && height) {
                minHeight = `${height}px`;
            }
            this.minHeight = minHeight
        }
    },
    mounted() {
        this.currentWidth = window.innerWidth;
        this.calcStyle(true);
        global.resize(this.calcStyle);
        if (
            !this.isMobileSize &&
            (this.sizeOption || {})['type'] === 'fullScreen'
        ) {
            this.$EventBus.$on('module-mounted', ({ $el }) => {
                if ($el.compareDocumentPosition(this.$el) === 2) {
                    this.calcStyle();
                }
            });
        }
        if (this.isMobileSize) {
            this.observer = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    this.calcStyle(true);
                });
            });
            const config = { attributes: true, childList: false, subtree: false };
            this.observer.observe(this.$el, config);
        }
    },
    methods: {
        getSrc(src) {
            if (src && typeof src === 'object' && !src.imgUrl) {
                src = null;
            }
            return src;
        },
        calcStyle(flag) {
            if (
                flag !== true &&
                this.isMobileSize &&
                this.currentWidth &&
                this.currentWidth === window.innerWidth
            ) {
                return; 
            }

            this.currentWidth = window.innerWidth;
            const sizeOption = this.sizeOption || {};
            let type = sizeOption.type;
            let ratio = sizeOption.ratio;
            let height = sizeOption.height;
            let isMobileSize = this.isMobileSize;
            let minHeight = 0;
            let autoHeight = 0;
            if (isMobileSize) {
                type = sizeOption.mobileType;
                ratio = sizeOption.mobileRatio;
                height = sizeOption.mobileHeight;
            }
            let offsetWidth = this.$el.offsetWidth;
            let offsetHeight = this.$el.offsetHeight;
            const videoOption = this.videoOption || {};
            const videoPlay = this.videoPlay;
            const videoRatio = this.videoRatio;
            let offsetTop = this.getOffsetTop(this.$el);
            // video size is different template7
            if (
                videoOption.autoHeight &&
                (videoOption.autoplay || videoOption.playInline) &&
                videoRatio &&
                videoPlay
            ) {
                //video height than width or width than height more
                if (videoRatio > 1) {
                    autoHeight = (offsetWidth / videoRatio);
                } else {
                    autoHeight = Math.min(offsetHeight, (offsetWidth / videoRatio));
                }
            }

            if (type === BANNER_TYPE.scale) {
                minHeight = (offsetWidth * ratio) / 100;
            } else if (type === BANNER_TYPE.fullScreen) {
                minHeight = Math.max(
                    window.innerHeight - offsetTop,
                    500
                );
            } else if (type === BANNER_TYPE.height) {
                minHeight = height || 0;
            }
            
            minHeight = Math.max(minHeight, autoHeight) + 'px';

            if (this.getBannerStyle) {
                let result = this.getBannerStyle({
                    offsetWidth,
                    videoOption,
                    videoRatio,
                    autoHeight,
                    height,
                    offsetTop,
                    type,
                    minHeight,
                    isMobileSize,
                    BANNER_TYPE
                });
                if (result) {
                    this.minHeight = result.minHeight;
                    return;
                }
            } 
            this.minHeight = minHeight;
        },
        getOffsetTop(el) {
            let top = 0;
            while (el.offsetParent) {
                top += el.offsetTop;
                el = el.offsetParent;
            }
            return top;
        },
        bannerClick(evt) {
            if (this.isVideo) {
                const bannerVideoVm = this.$refs.bannerVideo;
                const target = evt.target;
                if (util.closest(target, 'form') || evt.avoidEmitMedia === 1) {
                    return; 
                }
                if (
                    util.closest(target, 'video') &&
                    this.videoOption.showProgress
                ) {
                    return; 
                }
                if (bannerVideoVm && bannerVideoVm.isPlay) {
                    this.doVideoPause();
                } else if (bannerVideoVm) {
                    this.doVideoPlay();
                }
            }
        },
        doVideoPlay() {
            this.$refs.bannerVideo && this.$refs.bannerVideo.doPlay();
        },
        doVideoPause() {
            this.$refs.bannerVideo && this.$refs.bannerVideo.doPause();
        },
        doVideoStop() {
            this.$refs.bannerVideo && this.$refs.bannerVideo.doStop();
        },
        refreshVideo() {
            this.videoKey = new Date().getTime();
            this.videoRatio = 0;
            this.videoPlay = false;
        },
        updateVideoPlay(flag) {
            if (flag) {
                this.videoPlay = true;
            }
            this.$emit('updateVideoPlay', flag);
        },
        updateVideoRatio(ratio) {
            this.videoRatio = ratio;
        }
    },
    unmounted() {
        global.resize(this.calcStyle, true);
        this.observer && this.observer.disconnect();
    },
    components: {
        pictures: defineAsyncComponent(() =>
            import(
                /* webpackChunkName: "components/pictures"*/ './pictures.vue'
            )),
        bannerVideo: defineAsyncComponent(() =>
            import(
                /* webpackChunkName: "components/banner-video"*/ './banner-video.vue'
            ))
    }
};
</script>

<style lang="scss">
@import '../theme/media.scss';
.banner {
    position: relative;
    height: auto;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #fff;
    padding: 10px 0;
    .banner-mask {
        position: absolute;
        background: rgba(0,0,0,0.15);
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
        pointer-events: none;
    }
    .single-picture {
        overflow: hidden;
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        background-size: cover;
        background-position: center;
    }
    .swiper-pagination-bullet{
        width: 6px;
        height: 6px;
        margin: 0 3px!important;
        border-radius: 6px;
        background: #fff;
        opacity: 0.3;
        &.swiper-pagination-bullet-active{
            width: 12px;
            border-radius: 6px;
            opacity: 1;
        }
    }
    .default-wrap {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 10;
        background: rgba(218, 218, 218, 0.2);
        display: flex;
        justify-content: center;
        align-items: center; 
        img {
            width: 100px;
            height: 87.5px;
            object-fit: contain;
            opacity: 0.3;
            @include media-800 {
                width: 50px;
                height: 44px;
            }
        }
    }
}
</style>
